import { Accessor, createMemo, createSignal, For, JSX, Setter, Show } from "solid-js";
import { useWebStoreContext } from "~/context/webstore_context";
import { clientIdConfig } from "~/data/partner_config";
import { useOutsideClick } from "~/utils/hooks/use_outside_click";

export type ProfileDropdownProps = {
  isOpen: Accessor<boolean>;
  setIsOpen: Setter<boolean>;
  userName: string;
  userContact: string;
  onAction: (action: "my-gift-cards" | "help" | "terms" | "logout") => void;
  isMobile?: boolean;
  showLogout: boolean;
  showHelp?: boolean;
};

type MenuItem = {
  id: string;
  label: string;
  icon: JSX.Element;
  action: "my-gift-cards" | "help" | "terms" | "logout";
};

export function ProfileDropdown(props: ProfileDropdownProps) {
  const [dropdownRef, setDropdownRef] = createSignal<HTMLDivElement>();
  const [buttonRef, setButtonRef] = createSignal<HTMLButtonElement>();

  const { getClientId, checkIsOfferAvailed } = useWebStoreContext();

  useOutsideClick(
    dropdownRef,
    () => {
      props.setIsOpen(false);
    },
    buttonRef
  );

  const allMenuItems: MenuItem[] = [
    {
      id: "my-gift-cards",
      label: "My gift cards",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 5.83333H17.5M2.5 10H17.5M2.5 14.1667H17.5"
            stroke="currentColor"
            stroke-width="1.67"
            stroke-linecap="round"
            class="text-text-normal-secondary"
          />
        </svg>
      ),
      action: "my-gift-cards",
    },
    {
      id: "help",
      label: "Help & support",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z"
            stroke="currentColor"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-text-normal-secondary"
          />
          <path
            d="M7.57495 7.50001C7.77087 6.94306 8.15758 6.47344 8.66658 6.17426C9.17558 5.87507 9.77403 5.76579 10.3559 5.86561C10.9378 5.96543 11.4656 6.26792 11.8458 6.71963C12.2261 7.17134 12.4342 7.74296 12.4333 8.33334C12.4333 10 9.93328 10.8333 9.93328 10.8333"
            stroke="currentColor"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-text-normal-secondary"
          />
          <path
            d="M10 14.1667H10.0083"
            stroke="currentColor"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-text-normal-secondary"
          />
        </svg>
      ),
      action: "help",
    },
    {
      id: "terms",
      label: "Terms & conditions",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.6667 1.66667H5C4.55797 1.66667 4.13405 1.84226 3.82149 2.15482C3.50893 2.46738 3.33333 2.89131 3.33333 3.33334V16.6667C3.33333 17.1087 3.50893 17.5326 3.82149 17.8452C4.13405 18.1577 4.55797 18.3333 5 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66667L11.6667 1.66667Z"
            stroke="currentColor"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-text-normal-secondary"
          />
          <path
            d="M11.6667 1.66667V6.66667H16.6667"
            stroke="currentColor"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-text-normal-secondary"
          />
        </svg>
      ),
      action: "terms",
    },
  ];

  const menuItems = createMemo(() => {
    let items = [...allMenuItems];

    // Hide "My gift cards" for Mastercard Indriya clients
    if (getClientId() === clientIdConfig.mastercardIndriyaClientId) {
      items = items.filter((item) => item.id !== "my-gift-cards");
    }

    // Hide "My gift cards" for OneCard clients when offer has been availed
    if (
      getClientId() === clientIdConfig.onecardClientId &&
      checkIsOfferAvailed()
    ) {
      items = items.filter((item) => item.id !== "my-gift-cards");
    }

    // Hide help if showHelp is false
    if (props.showHelp === false) {
      items = items.filter((item) => item.id !== "help");
    }

    return items;
  });

  const logoutMenuItem: MenuItem = {
    id: "logout",
    label: "Log out",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5"
          stroke="currentColor"
          stroke-width="1.67"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-text-normal-secondary"
        />
        <path
          d="M13.3333 14.1667L17.5 10L13.3333 5.83334"
          stroke="currentColor"
          stroke-width="1.67"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-text-normal-secondary"
        />
        <path
          d="M17.5 10H7.5"
          stroke="currentColor"
          stroke-width="1.67"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-text-normal-secondary"
        />
      </svg>
    ),
    action: "logout",
  };

  const handleMenuClick = (
    action: "my-gift-cards" | "help" | "terms" | "logout"
  ) => {
    props.onAction(action);
    props.setIsOpen(false);
  };

  return (
    <div class="relative">
      {/* Profile Button - Desktop only */}
      <button
        ref={setButtonRef}
        class="flex h-8 w-8 items-center justify-center rounded-full border border-stroke-2 bg-background-normal-primary transition-colors hover:bg-background-normal-secondary"
        onClick={() => props.setIsOpen(!props.isOpen())}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M16.6667 18.3335H15V16.6668C15 16.0038 14.7366 15.3679 14.2678 14.8991C13.799 14.4302 13.1631 14.1668 12.5 14.1668H7.50004C6.837 14.1668 6.20111 14.4302 5.73227 14.8991C5.26343 15.3679 5.00004 16.0038 5.00004 16.6668V18.3335H3.33337V16.6668C3.33337 15.5618 3.77236 14.502 4.55376 13.7206C5.33516 12.9391 6.39497 12.5002 7.50004 12.5002H12.5C13.6051 12.5002 14.6649 12.9391 15.4463 13.7206C16.2277 14.502 16.6667 15.5618 16.6667 16.6668V18.3335ZM10 10.8335C9.34343 10.8335 8.69325 10.7042 8.08662 10.4529C7.48 10.2016 6.9288 9.83332 6.46451 9.36903C6.00021 8.90474 5.63192 8.35354 5.38064 7.74691C5.12937 7.14029 5.00004 6.49011 5.00004 5.8335C5.00004 5.17689 5.12937 4.52671 5.38064 3.92008C5.63192 3.31345 6.00021 2.76226 6.46451 2.29796C6.9288 1.83367 7.48 1.46537 8.08662 1.2141C8.69325 0.962825 9.34343 0.833496 10 0.833496C11.3261 0.833496 12.5979 1.36028 13.5356 2.29796C14.4733 3.23564 15 4.50741 15 5.8335C15 7.15958 14.4733 8.43135 13.5356 9.36903C12.5979 10.3067 11.3261 10.8335 10 10.8335ZM10 9.16683C10.8841 9.16683 11.7319 8.81564 12.3571 8.19052C12.9822 7.5654 13.3334 6.71755 13.3334 5.8335C13.3334 4.94944 12.9822 4.10159 12.3571 3.47647C11.7319 2.85135 10.8841 2.50016 10 2.50016C9.11599 2.50016 8.26814 2.85135 7.64302 3.47647C7.0179 4.10159 6.66671 4.94944 6.66671 5.8335C6.66671 6.71755 7.0179 7.5654 7.64302 8.19052C8.26814 8.81564 9.11599 9.16683 10 9.16683Z"
            class="fill-text-normal-primary"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      <Show when={props.isOpen()}>
        <div
          ref={setDropdownRef}
          class="absolute right-0 top-full z-10 mt-2 w-[280px] rounded-xl border border-stroke-1 bg-background-mapping-elevation-1 shadow-[0px_16px_32px_-12px_rgba(14,18,27,0.08)]"
        >
          <div class="flex flex-col p-1">
            {/* User Info Section */}
            <Show when={props.userName || props.userContact}>
              <div class="flex items-center gap-3 rounded-lg  px-3 py-2.5">
                {/* Avatar */}
                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-background-normal-secondary">
                  <span class="text-para-3-regular text-text-normal-secondary">
                    {props.userName?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>

                {/* User Details */}
                <div class="flex flex-1 flex-col gap-0">
                  <Show when={props.userName}>
                    <span class="truncate text-para-3-medium text-text-normal-primary">
                      {props.userName}
                    </span>
                  </Show>
                  <Show when={props.userContact}>
                    <span class="truncate text-label-regular text-text-normal-secondary">
                      {props.userContact}
                    </span>
                  </Show>
                </div>
              </div>

              {/* Separator */}
              <div class="my-1 h-px bg-stroke-1" />
            </Show>

            {/* Menu Items */}
            <For each={menuItems()}>
              {(item) => (
                <button
                  class="flex items-center gap-3 rounded-lg px-3 py-2.5 !transition-none hover:bg-background-normal-secondary"
                  onClick={() => handleMenuClick(item.action)}
                >
                  <div class="flex h-5 w-5 items-center justify-center">
                    {item.icon}
                  </div>
                  <span class="text-para-3-medium text-text-normal-primary">
                    {item.label}
                  </span>
                  <div class="ml-auto">
                    <ChevronRightIcon />
                  </div>
                </button>
              )}
            </For>

            {/* Logout Item */}
            <Show when={props.showLogout}>
              <div class="my-1 h-px bg-stroke-1" />
              <button
                class="flex items-center gap-3 rounded-lg px-3 py-2.5 !transition-none hover:bg-background-normal-secondary"
                onClick={() => handleMenuClick(logoutMenuItem.action)}
              >
                <div class="flex h-5 w-5 items-center justify-center">
                  {logoutMenuItem.icon}
                </div>
                <span class="text-para-3-medium text-text-normal-primary">
                  {logoutMenuItem.label}
                </span>
                <div class="ml-auto">
                  <ChevronRightIcon />
                </div>
              </button>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.5 15L12.5 10L7.5 5"
        stroke="currentColor"
        stroke-width="1.67"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-text-normal-tertiary"
      />
    </svg>
  );
}
